import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listPlatforms,
  getPlatform,
  createPlatform,
  updatePlatform,
  deletePlatform,
  getPlatformConfig,
  getMetaPlatform,
  healthCheck,
} from '../services/api';
import type { CreatePlatformInput, UpdatePlatformInput } from '../types';

// Query keys
export const platformKeys = {
  all: ['platforms'] as const,
  lists: () => [...platformKeys.all, 'list'] as const,
  list: (filters?: any) => [...platformKeys.lists(), filters] as const,
  details: () => [...platformKeys.all, 'detail'] as const,
  detail: (id: string) => [...platformKeys.details(), id] as const,
  config: (id: string) => [...platformKeys.detail(id), 'config'] as const,
};

export const metaPlatformKeys = {
  all: ['meta-platform'] as const,
  detail: () => [...metaPlatformKeys.all, 'detail'] as const,
};

export const healthKeys = {
  check: ['health'] as const,
};

// Platforms
export function usePlatforms() {
  return useQuery({
    queryKey: platformKeys.lists(),
    queryFn: async () => {
      const result = await listPlatforms();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch platforms');
      }
      return result.data || [];
    },
    staleTime: 30000, // 30 seconds
  });
}

export function usePlatform(id: string) {
  return useQuery({
    queryKey: platformKeys.detail(id),
    queryFn: async () => {
      const result = await getPlatform(id);
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch platform');
      }
      return result.data;
    },
    enabled: !!id,
  });
}

export function usePlatformConfig(id: string) {
  return useQuery({
    queryKey: platformKeys.config(id),
    queryFn: async () => {
      const result = await getPlatformConfig(id);
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch platform config');
      }
      return result.data;
    },
    enabled: !!id,
  });
}

export function useCreatePlatform() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePlatformInput) => {
      const result = await createPlatform(input);
      if (!result.success) {
        throw new Error(result.error || 'Failed to create platform');
      }
      return result.data;
    },
    onSuccess: () => {
      // Invalidate and refetch platforms list
      queryClient.invalidateQueries({ queryKey: platformKeys.lists() });
    },
  });
}

export function useUpdatePlatform() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdatePlatformInput }) => {
      const result = await updatePlatform(id, input);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update platform');
      }
      return result.data;
    },
    onSuccess: (_, variables) => {
      // Invalidate specific platform and list
      queryClient.invalidateQueries({ queryKey: platformKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: platformKeys.lists() });
    },
  });
}

export function useDeletePlatform() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await deletePlatform(id);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete platform');
      }
      return result.data;
    },
    onSuccess: () => {
      // Invalidate platforms list
      queryClient.invalidateQueries({ queryKey: platformKeys.lists() });
    },
  });
}

// Meta Platform
export function useMetaPlatform() {
  return useQuery({
    queryKey: metaPlatformKeys.detail(),
    queryFn: async () => {
      const result = await getMetaPlatform();
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch meta-platform');
      }
      return result.data;
    },
    staleTime: 60000, // 1 minute
  });
}

// Health Check
export function useHealthCheck() {
  return useQuery({
    queryKey: healthKeys.check,
    queryFn: async () => {
      const result = await healthCheck();
      if (!result.success) {
        throw new Error(result.error || 'Failed to perform health check');
      }
      return result.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // 10 seconds
  });
}
